import { defineStore } from 'pinia'
import subscriptionService from '@/services/subscription.service'
import onboardingService from '@/services/onboarding.service'

export const useSubscriptionStore = defineStore({
  id: 'subscription',

  state: () => ({
    currentSubscription: null,
    trialAvailable: false,
    packages: [],
    addons: [],
    bundles: [],
    activeAddons: [],
    activeFeatures: [],
    history: [],
    transactionDetail: null,
    loading: false,
    historyLoading: false,
    detailLoading: false,
    error: null,
  }),

  getters: {
    status: state => state.currentSubscription?.status || 'none',

    isActive: state => ['active', 'warning', 'trial'].includes(state.currentSubscription?.status),

    isPaused: state => state.currentSubscription?.status === 'paused',

    isReadOnly: state => ['grace', 'paused'].includes(state.currentSubscription?.status),

    isLocked: state => ['suspended', 'cancelled'].includes(state.currentSubscription?.status)
      || !state.currentSubscription,

    currentPackage: state => state.currentSubscription?.package || null,

    daysRemaining: state => state.currentSubscription?.days_remaining ?? 0,

    planPackages: state => state.packages,

    addonPackages: state => state.addons,

    bundlePackages: state => state.bundles,

    hasFeature: state => flag => state.activeFeatures.includes(flag),

    hasTrialUsed: state => !state.trialAvailable,
  },

  actions: {
    async loadSubscription() {
      this.loading = true
      this.error = null
      try {
        const { data } = await subscriptionService.getStatus()
        const result = data.data || {}
        this.currentSubscription = result.subscription || null
        this.trialAvailable = result.trial_available ?? false
        // Use pre-computed active_features from backend when available
        if (Array.isArray(result.active_features)) {
          this.activeFeatures = result.active_features
        }
        // Load active addons (for display + fallback feature merge)
        this.loadAddons()
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat status langganan'
        this.currentSubscription = null
        this.trialAvailable = false
      } finally {
        this.loading = false
      }
    },

    async loadPackages() {
      this.loading = true
      try {
        const { data } = await subscriptionService.getPackages()
        const result = data.data || {}
        this.packages = result.plans || []
        this.addons = result.addons || []
        this.bundles = result.bundles || []
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat daftar paket'
        this.packages = []
        this.addons = []
        this.bundles = []
      } finally {
        this.loading = false
      }
    },

    async loadAddons() {
      try {
        const { data } = await subscriptionService.getEntityAddons()
        const result = data.data || {}
        this.activeAddons = result.addons || []

        // Normalize features from {key: bool} map or flat string array to flat enabled array
        const normalizeFeatures = (raw) => {
          if (!raw) return []
          if (Array.isArray(raw)) return raw
          return Object.entries(raw).filter(([, v]) => v).map(([k]) => k)
        }

        const planFeatures = normalizeFeatures(this.currentPackage && this.currentPackage.features)
        const addonFeatures = this.activeAddons.flatMap(a => normalizeFeatures(a.package && a.package.features))
        this.activeFeatures = [...new Set([...planFeatures, ...addonFeatures])]
      } catch (err) {
        this.activeAddons = []
        // Keep activeFeatures if already set from backend active_features
      }
    },

    async loadHistory(params = {}) {
      this.historyLoading = true
      try {
        const { data } = await subscriptionService.getHistory(params)
        this.history = data.data?.data || data.data || []
        return data.data
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat riwayat'
      } finally {
        this.historyLoading = false
      }
    },

    async startTrial(withDemoData = false) {
      this.loading = true
      try {
        const { data } = await onboardingService.startTrial(withDemoData)
        this.trialAvailable = false
        return data
      } catch (err) {
        throw err
      } finally {
        this.loading = false
      }
    },

    async loadTransactionDetail(id) {
      this.detailLoading = true
      this.transactionDetail = null
      try {
        const { data } = await subscriptionService.getTransactionDetail(id)
        this.transactionDetail = data.data || null
        return data.data
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat detail transaksi'
        this.transactionDetail = null
      } finally {
        this.detailLoading = false
      }
    },

    async toggleAutoRenew() {
      try {
        const { data } = await subscriptionService.toggleAutoRenew()
        if (this.currentSubscription) {
          this.currentSubscription.auto_renew = data.data?.auto_renew
        }
        return data
      } catch (err) {
        throw err
      }
    },

    async pauseSubscription() {
      this.loading = true
      try {
        const { data } = await subscriptionService.pause()
        // Reload to get fresh subscription state
        await this.loadSubscription()
        return data
      } catch (err) {
        throw err
      } finally {
        this.loading = false
      }
    },
  },
})
